import React, { useState, useEffect, useCallback } from 'react'
import PropTypes from 'prop-types'
import sortBy from 'sort-by'
import api from '../../services/api'
import { hasPermission } from '../../utils/hasPermission'
import { createQueryString } from '../../utils/queryParams'
import {
  USER_PERMISSIONS,
  FLOW_MONITOR_ALERT_TYPE_LABELS,
  FLOW_MONITOR_ALERT_TYPE_DESCRIPTIONS,
} from '../../constants'

import { H3 } from '../common/Headers'
import {
  Spin,
  Switch,
  AntSelect,
  Table,
  Column,
  Icon,
  Tooltip,
  Popover,
} from '../common/Ant'
import { Option } from '../common/Select'
import { Anchor } from '../common/Anchor'
import Button from '../common/Button'
import SiteUsers from '../SiteUsers'

const FlowMonitorAlertsSettings = ({
  siteSlug,
  siteUsers,
  getAllUsersBySite,
}) => {
  const [isLoading, setIsLoading] = useState(true)
  const [recipients, setRecipients] = useState([])
  const [subscriptions, setSubscriptions] = useState([])
  const [selectedUserId, setSelectedUserId] = useState(undefined)
  const [flowMonitors, setFlowMonitors] = useState([])
  const [isFlowMonitorsLoading, setIsFlowMonitorsLoading] = useState(true)
  const [showUnassigned, setShowUnassigned] = useState(false)

  const canEdit = hasPermission(USER_PERMISSIONS.EDIT_FLOW_MONITOR)

  const getSettings = useCallback(() => {
    api
      .getFlowMonitorAlertSettings(siteSlug)
      .then(data => {
        setRecipients(data.recipients)
        setSubscriptions(data.subscriptions)
        setIsLoading(false)
      })
      .catch(e => {
        console.error(e)
        setIsLoading(false)
      })
  }, [siteSlug])

  useEffect(() => {
    getSettings()
  }, [siteSlug, getSettings])

  useEffect(() => {
    getAllUsersBySite()
  }, [siteSlug]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setIsFlowMonitorsLoading(true)
    api
      .getAllFlowMonitors(createQueryString({ siteSlug, perPage: 'all' }))
      .then(data => {
        setFlowMonitors(data.items)
        setIsFlowMonitorsLoading(false)
      })
      .catch(e => {
        console.error(e)
        setIsFlowMonitorsLoading(false)
      })
  }, [siteSlug])

  const handleAddRecipient = () => {
    if (!selectedUserId) return

    api
      .addFlowMonitorAlertRecipient(siteSlug, selectedUserId)
      .then(() => {
        setSelectedUserId(undefined)
        getSettings()
      })
      .catch(e => console.error(e))
  }

  const handleRemoveRecipient = recipientId => {
    api
      .removeFlowMonitorAlertRecipient(siteSlug, recipientId)
      .then(() => getSettings())
      .catch(e => console.error(e))
  }

  const handleToggleSubscription = (alertType, active) => {
    api
      .updateFlowMonitorAlertSubscription(siteSlug, alertType, active)
      .then(() => getSettings())
      .catch(e => console.error(e))
  }

  if (isLoading) {
    return <Spin size="large" className="w-100 center mv5" />
  }

  const availableUsers = siteUsers.filter(
    user => !recipients.some(recipient => recipient.userId === user.id)
  )

  const recipientUsers = recipients.map(recipient => {
    const user = siteUsers.find(u => u.id === recipient.userId)

    return {
      ...user,
      id: recipient.id,
      slug: (user && user.slug) || `recipient-${recipient.id}`,
      fullName: (user && user.fullName) || recipient.userName,
    }
  })

  const visibleFlowMonitors = showUnassigned
    ? flowMonitors
    : flowMonitors.filter(flowMonitor => flowMonitor.locationId)

  return (
    <div className="pa black-80">
      <div className="mb3 ba b--black-10 br3 pa3 bg-white shadow-4">
        <H3 className="ma0 mb2 f4">Flow Monitor Alert Recipients</H3>
        {canEdit && (
          <div className="flex items-center mb3">
            <AntSelect
              className="w-40 mr2"
              showSearch
              allowClear
              placeholder="Select a user"
              value={selectedUserId}
              onChange={setSelectedUserId}
              optionFilterProp="children"
            >
              {availableUsers.map(user => (
                <Option value={user.id} key={user.id}>
                  {user.firstName} {user.lastName}
                </Option>
              ))}
            </AntSelect>
            <Button
              text="Add Recipient"
              onClick={handleAddRecipient}
              disabled={!selectedUserId}
            />
          </div>
        )}
        <SiteUsers
          users={recipientUsers}
          getAllUsersBySite={getAllUsersBySite}
          deleteUserFromSite={canEdit ? handleRemoveRecipient : undefined}
        />
      </div>

      <div className="mb3 ba b--black-10 br3 pa3 bg-white shadow-4">
        <H3 className="ma0 mb2 f4">Alerts</H3>
        {subscriptions.map(subscription => (
          <div
            key={subscription.alertType}
            className="flex justify-between items-center pv1 bb b--black-10"
          >
            <span>
              {FLOW_MONITOR_ALERT_TYPE_LABELS[subscription.alertType] ||
                subscription.alertType}
              {FLOW_MONITOR_ALERT_TYPE_DESCRIPTIONS[subscription.alertType] && (
                <Popover
                  content={
                    <div style={{ maxWidth: 260 }}>
                      {FLOW_MONITOR_ALERT_TYPE_DESCRIPTIONS[
                        subscription.alertType
                      ]}
                    </div>
                  }
                  trigger="click"
                >
                  <Icon type="question-circle-o" className="ml2 pointer" />
                </Popover>
              )}
            </span>
            <Switch
              checked={subscription.active}
              disabled={!canEdit}
              onChange={checked =>
                handleToggleSubscription(subscription.alertType, checked)
              }
            />
          </div>
        ))}
      </div>

      <div className="mb3 ba b--black-10 br3 pa3 bg-white shadow-4">
        <div className="flex justify-between items-center mb2">
          <H3 className="ma0 f4">Flow Monitors</H3>
          <div className="flex items-center">
            <span className="mr2">Display Unassigned Flow Monitors</span>
            <Switch checked={showUnassigned} onChange={setShowUnassigned} />
          </div>
        </div>
        {isFlowMonitorsLoading ? (
          <Spin size="large" className="w-100 center mv4" />
        ) : (
          <Table
            dataSource={visibleFlowMonitors}
            rowKey="id"
            pagination={false}
            bordered
            tableLayout="auto"
          >
            <Column
              title="Pillar ID"
              dataIndex="pillarId"
              sorter={sortBy('pillarId')}
              render={(text, record) => (
                <Anchor to={`/inventory/flow-monitors/${record.id}`}>
                  {text}
                </Anchor>
              )}
            />
            <Column
              title="Module ID"
              dataIndex="moduleId"
              sorter={sortBy('moduleId')}
            />
            <Column
              title="Building"
              dataIndex="buildingName"
              sorter={sortBy('buildingName')}
              render={(text, record) =>
                text && record.buildingId ? (
                  <Anchor
                    to={`/sites/${siteSlug}/buildings/${record.buildingId}`}
                  >
                    {text}
                  </Anchor>
                ) : (
                  text
                )
              }
            />
            <Column
              title="Floor"
              dataIndex="floorName"
              sorter={sortBy('floorName')}
              render={(text, record) =>
                text && record.floorId ? (
                  <Anchor to={`/sites/${siteSlug}/floors/${record.floorId}`}>
                    {text}
                  </Anchor>
                ) : (
                  text
                )
              }
            />
            <Column
              title="Location"
              dataIndex="locationName"
              sorter={sortBy('locationName')}
              render={(text, record) =>
                text && record.floorId && record.locationId ? (
                  <Anchor
                    to={`/sites/${siteSlug}/floors/${record.floorId}/locations/${record.locationId}`}
                  >
                    {text}
                  </Anchor>
                ) : (
                  text
                )
              }
            />
            <Column
              title="Status"
              dataIndex="online"
              sorter={sortBy('online')}
              render={online =>
                online ? (
                  <Tooltip title="Online">
                    <Icon type="check-circle-o" className="green" />
                    <span className="pl2">Online</span>
                  </Tooltip>
                ) : (
                  <Tooltip title="Offline">
                    <Icon type="poweroff" className="red" />
                    <span className="pl2">Offline</span>
                  </Tooltip>
                )
              }
            />
          </Table>
        )}
      </div>
    </div>
  )
}

FlowMonitorAlertsSettings.propTypes = {
  siteSlug: PropTypes.string.isRequired,
  siteUsers: PropTypes.arrayOf(PropTypes.object).isRequired,
  getAllUsersBySite: PropTypes.func.isRequired,
}

export default FlowMonitorAlertsSettings
